import React, { useEffect, useState, useRef, useMemo, useContext } from "react"
import styles from "./index.module.scss"
import moment from 'moment'
import debounce from 'lodash.debounce';
import JSBridge from "@xpeng/js-bridge"
import useQuery from "@/hooks/useQuery"
import useECharts from "@/hooks/useEcharts"
import MessageBox from "@/components/message-box"
import {
  PROP_DICT,
  formatTripDuration,
  getDaysApart,
  formatHighlightData,
  formatAdParkingData
} from '../config'
import type { HightLightDataType } from '../config'
import {
  formatTimestampToTxt,
  formatVehiclePlate,
  desensitizedVin,
  disableShareOpt,
  closeWebview,
  jumpPage
} from '@/utils/common'
import { reportData } from "@/utils/report-data"
import {
  ErrorPlaceholder,
  EmptyPlaceholder,
} from "@/components/placeholder"
import LoadingPage from "@/components/loading-page"
import LoadingMore from '@/components/loading-more'
import Picker from '@/components/Picker'

import {
  TimeRangeItem
} from './../types'
import './reset.scss'
import './_variables.css'

import Alert from '@xp-design/mobile/esm/alert'
import '@xp-design/mobile/esm/alert/style'

import {
  getTripsList,
  getTripsSummary,
  getTripsHighlights,
  getUserVehicleInfo,
  postRemoveTripData,
  getEnableRecordStatus,
  postSwitchEnableRecord,
  getAnnouncement,
  getSettingsQuery
} from '@/api/trips-report'

import { useRouteMatch } from 'react-router-dom';
import { ThemeModeContext } from "../../../ThemeModeContext"
import { isSupportLocationDisplay } from "../config"
import IntelligentDriveData from '../components/IntelligentDriveData';

const DEFAULT_ERROR_MSG = '服务器开小差啦'

const LIST_FETCH_SIZE = 10

const App = () => {
  // 颜色主题
  const themeModeContext = useContext(ThemeModeContext)
  const themeMode = themeModeContext?.mode || 'light'

  // 判断是否支持位置展示
  const isSupportLocation = isSupportLocationDisplay()
  
  // 从车控入口进，会携带vin参数
  const { path } = useRouteMatch()

  const queryVin = useQuery().get('vin') || ''
  const queryMonth = useQuery().get('month') || ''
  const isSuperErev = useQuery().get('isSuperErev') === 'true'
  const isValidateMonth = useRef(false) // 是否是有效的月份
  const currentSearchTimeRange = useRef<number[]>([0, 0])
  // 跳转行程页面防抖
  const debouncedClickRef = useRef(
    debounce((value, vehicleInfo) => {
      jumpToTripPage(value, vehicleInfo)
    }, 300)
  )
  // 是否支持实时行程
  const allowRealTimeTrip = useRef(false)

  const [beforeInit, setBeforeInit] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingError, setIsLoadingError] = useState(false)
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [announcementInfo, setAnnouncementInfo] = useState<any>(null)

  // echarts
  const lineChartRef = useRef<any>(null)
  const [lineData, setLineData] = useState<any[]>([])
  const [xLabels, setXLabels] = useState<string[]>([])

  // picker
  const timeRangeOptions = useRef<TimeRangeItem[]>([])
  const [showDateRangePicker, setShowDateRangePicker] = useState(false)
  const [dateValues, setDateValues] = useState<any>({ label: '' })
  const [dateOptions, setDateOptions] = useState<any>({ label: [] })

  // data
  const [currentCycle, setCurrentCycle] = useState('')
  const [listData, setListData] = useState<any[][]>([])
  const [tripsData, setTripsData] = useState<any>({})
  const [highlightsData, setHighlightsData] = useState<HightLightDataType | undefined>()
  const [vehicleInfo, setVehicleInfo] = useState({
    vin: '',
    plateNo: '',
    vehicleTypeSaleName: '',
    validStartTime: -1
  })

  // 上拉加载行程列表
  const finishLoadList = useRef(false)
  const isLoadingList = useRef(false)
  const [loadingList, setLoadingList] = useState(false)

  // 折线图Option
  const lineChartOption = useMemo(() => {
    const defaultColorMap = {
      xLineColor: 'rgba(0, 0, 0, 0.1)',
      xLabelsColor: 'rgba(0, 0, 0, 0.26)',
      yLineColor: 'rgba(0, 0, 0, 0.2)',
      yLabelsColor: '#B3B3B3',
      tripColor: '#ADB0B6',
      adTripColor: '#A4CE4C',
      legengTextColor: 'rgba(0, 0, 0, 0.4)',
      tooltipBgColor: '#FFFFFF'
    }
    const darkColorMap = {
      xLineColor: 'rgba(255, 255, 255, 0.25)',
      xLabelsColor: 'rgba(255, 255, 255, 0.25)',
      yLineColor: 'rgba(255, 255, 255, 0.1)',
      yLabelsColor: '#B3B3B3',
      tripColor: '#ADB0B6',
      adTripColor: '#7EA34B',
      legengTextColor: 'rgba(255, 255, 255, 0.4)',
      tooltipBgColor: '#1A1B1F'
    }
    const colorMap = themeMode === 'dark' ? darkColorMap : defaultColorMap
    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: colorMap.tooltipBgColor,
        borderColor: colorMap.tooltipBgColor,
        formatter: function (params) {
          let tip = `<div>${lineData[0].timeList[params[0].dataIndex]}</div>`
          const marker = (color) => `<span style="display:inline-block;margin-right:6px;width:10px;height:10px;background-color:${color};border-radius:1px"></span>`
          if (params !== null && params.length > 0) {
            for (let i = 0; i < params.length; i++) {
              tip += marker(params[i].color) + `<span style="font-size:12px">${lineData[i].timeRange}</span>&nbsp&nbsp&nbsp<span style="font-size:12px">${params[i].value}</span><br />`
            }
          }
          return params[0]?.value ? tip : ''
        }
      },
      legend: {
        data: lineData.map(item => {
          return {
            icon: 'roundRect',
            name: item.timeRange
          }
        }),
        selectedMode: false,
        y: 'bottom',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 16,
        textStyle: {
          fontSize: 12,
          // color: 'rgba(0,0,0,0.4)'
          color: colorMap.legengTextColor
        }
      },
      grid: {
        top: '5%',
        left: '6%',
        right: '7%',
        bottom: '16%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        axisTick: { show: false },
        axisLabel: {
          color: colorMap.xLabelsColor,
          fontSize: '10px',
          margin: 15,
          interval: 0 // 强制显示所有标签
        },
        axisLine: {
          lineStyle: {
            color: colorMap.xLineColor
          }
        },
        data: xLabels
      },
      yAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: {
            color: colorMap.yLineColor,
            type: 'dashed',
            width: 0.5
          }
        },
        axisLabel: {
          color: colorMap.yLabelsColor,
          fontSize: '10px',
          // formatter: function (value, index) {
          //   return value.toFixed(1)
          // }
        }
      },
      color: lineData.length > 1 ? [colorMap.tripColor, colorMap.adTripColor] : [colorMap.tripColor],
      series: lineData.map(item => {
        return {
          name: item.timeRange,
          type: 'line',
          data: item.data
        }
      })
    }
  }, [lineData, xLabels,themeMode])

  /**
   * 根据数据开始时间决定往前推的月份，格式为[{ value: timestamp[], label: string }]
   * 包括最近7天、xxxx年x月
   * 数据开始时间落在哪一天，就展示到哪一天的月份
   * @param validStartTime
   * @param allowRealTimeTrip 是否支持实时
   */
  const getTimeRangeOptions = (validStartTime: number, allowRealTimeTrip: boolean) => {
    const options: TimeRangeItem[] = []

    // 当天0点时间戳
    const todayTimestamp = new Date().setHours(0,0,0,0)
    // 当前时间的前7天的日期
    const recent7DaysBefore = moment(todayTimestamp, 'x').subtract(7, "days")
    // 获取昨天23:59:59的时间戳
    const yesterdayTimestamp = todayTimestamp - 1000
    // 获取现在时间戳 （实时需传入现在）
    const nowTimestamp = new Date().getTime()
    const endTimeVlaue = allowRealTimeTrip ? +nowTimestamp : +yesterdayTimestamp    
    // 先push最近7天选项
    options.push({
      value: [+recent7DaysBefore.format('x'), endTimeVlaue],
      label: `最近7天（${formatTimestampToTxt(+recent7DaysBefore.format('x'), 'MM/DD')}-${formatTimestampToTxt(+yesterdayTimestamp, 'MM/DD')}）`
    })

    // 判断数据开始时间落在几月份，距今几个月，push对应月份选项
    const todayDate = new Date(todayTimestamp)
    const startDate = new Date(validStartTime)
    const thisYear = todayDate.getFullYear()
    const startYear = startDate.getFullYear()
    const thisMonth = todayDate.getMonth() + 1 
    const startMonth = startDate.getMonth() + 1
    // 数据开始时间所在月份距今几个月
    const monthNum = (thisYear - startYear) * 12 + (thisMonth - startMonth)
    // 距今有哪些月份（包括当月），存储每个月的1号，格式YYYY-MM-DD
    let monthArr = [`${thisYear}-${thisMonth}-01`]
    for (let i = 1; i <= monthNum; i++) {
      if (thisMonth - i <= 0) {
        // 判断间隔多少年
        const minuendY = Math.floor(i / 12) + (i % 12 >= thisMonth ? 1: 0)
        const Y = thisYear - minuendY
        const M = (thisMonth - i) % 12 + 12
        monthArr.push(`${Y}-${M}-01`)
      } else {
        const Y = thisYear
        const M = thisMonth - i
        monthArr.push(`${Y}-${M}-01`)
      }
    }

    monthArr?.length && monthArr.forEach(m => {
      // 月份不足两位的补0 防止ios端报错
      const mArr = m.split('-')
      mArr[1].length === 1 && (mArr[1] = '0' + mArr[1])
      m = mArr.join('-')

      const firstDayDate = new Date(m)
      const lastDayDate = new Date(m)
      lastDayDate.setMonth(lastDayDate.getMonth() + 1) // 下个月的1号
      const firstDayTs = new Date(firstDayDate.setDate(1)).setHours(0,0,0,0) // 起始日期当天0点
      const lastDayTs = new Date(lastDayDate.setDate(0)).setHours(0,0,0,0) + 24 * 3600 * 1000 - 1000 // 结束日期当天23:59:59

      options.push({
        value: [firstDayTs, lastDayTs],
        label: `${firstDayDate.getFullYear()}年${firstDayDate.getMonth() + 1}月`
      })
    })

    timeRangeOptions.current = options

    console.log('month options --> ', options)
  }

  useECharts(lineChartRef, lineChartOption, isLoading)

  useEffect(() => {
    document.title = '行程记录'

    bridgeListener()
    onRefreshListener()

    // setTimeout(() => {
    //   defineSelfBtnAction()
    // }, 0)
    defineSelfBtnAction() // 点击返回按钮时，需要调用
    setShareBtn() // 页面未渲染完成时，调用

    fetchEnableStatus()

    // disableShareOpt()

    addListener()

    getAnnouncementBar()

    // unMount
    return () => {
      removeListener()
      debouncedClickRef.current.cancel() // 取消防抖
    }
  }, [])

  const getAnnouncementBar = async () => {
    try {
      const res: any = await getAnnouncement({
        platform: 'H5',
        positionCodes: 'ALERT_TRIP_H5'
      })
      
      const lastDeployId = localStorage.getItem('deployId')
      if (lastDeployId === res?.ALERT_TRIP_H5?.deployId) {
        setAnnouncementInfo(null)
        return
      }
      
      if (res?.ALERT_TRIP_H5 && res.ALERT_TRIP_H5?.children?.length > 0) {
        const announcement = res.ALERT_TRIP_H5.children?.[0]
        if (announcement?.content) {
          setAnnouncementInfo({
            ...announcement.content,
            deployId: res?.ALERT_TRIP_H5?.deployId
          })
        }
      }
    } catch (error) {
      console.log('getAnnouncementBar error --> ', error)
    }
  }

  const bridgeListener = () => {
    JSBridge.registerHandler('onResult', function(data, responseCallback) {
      console.log('onResult --> ', data)
      // 监听设置页关闭行程，用于关闭当前webview
      if (data.result?.result === 'closeWindow') {
        closeWebview()
      }
      // 若设置页开启了行程记录，刷新页面（为ios专做，安卓返回会自动刷新）
      if (data.result?.result === 'reload') {
        window.location.reload()
      }
    })
  }
  // 从行程页或者设置页返回的时候 对比displayAddress的状态 如果不一致 需要刷新页面
  const onRefreshListener = () => {
    JSBridge.registerHandler('onRefresh', (data, responseCallback) => {
      console.log('onRefresh --> ', data)
      getSettingQueryPromise()
        .then(res => {
          const sessiionDisplayAddress = sessionStorage.getItem('displayAddress')
          if (sessiionDisplayAddress ===  null || sessiionDisplayAddress === undefined) {
            return
          }
          const displayAddress = JSON.parse(sessiionDisplayAddress)
          if (res?.moduleEnabled && res?.displayAddress !== displayAddress) {
            sessionStorage.setItem('displayAddress', res?.displayAddress)
            initData()
          }
        })
    })
  }

  // 这个方法在深色模式以后兼容以后 行不通了 因为页面加载就会执行 
  // 但是我们页面还在调getthemeMode 所以webview调用不了我们的方法
  // 但是这个方法目前还是要调用 因为返回的时候 是需要调用的
  const defineSelfBtnAction = () => {
    console.log('defineSelfBtnAction ---->')
    JSBridge.registerHandler('onActionButtonShowing', (data, responseCallback) => {
      console.log(data, 'onActionButtonShowing');
      const options: any = {}
      options.share = false
      const pathname = path.replace('home', 'setting')
      options.button = {
        // icon_url: require('../assets/setting.png'),
        icon_url: themeMode === 'dark'? require('../assets/setting_dark.png') : require('../assets/setting.png'),
        link_url: `${window.location.origin}${pathname}?vin=${queryVin}`,
        width: 25,
        height: 25
      }
      responseCallback(options)
    })
  }
  const setShareBtn = () => {
    console.log('setShareBtn---->')
    const options: any = {}
    // true = 原生分享按钮显示, false = 原生分享按钮隐藏
    options.share = false
    const pathName = path.replace('home', 'setting')
    options.button = {
      icon_url: themeMode === 'dark'? require('../assets/setting_dark.png') : require('../assets/setting.png'),
      link_url: `${window.location.origin}${pathName}?vin=${queryVin}`,
      width: 25,
      height: 25
    }
    JSBridge.callHandler('setNavigationBtnShow', options)
      .then(res => {
        console.log('setNavigationBtnShow res --> ', res);
      })
  }
  
  const showViewRecordConfirm = () => {
    MessageBox.confirm({
      title: '行程记录功能开启',
      message: '开启后从当天开始记录里程、时长、位置等信息',
      cancelButtonText: '暂不开启',
      confirmButtonText: '确认开启'
    }).then(() => {
      postSwitchEnableRecord({ moduleEnabled: true, displayAddress: true }, queryVin)
        .then(res => {
          if (res.code === 200) {
            initData()
          } else {
            MessageBox.toast(DEFAULT_ERROR_MSG)
          }
        }).catch(err => {
          MessageBox.toast(DEFAULT_ERROR_MSG)
        })
    }).catch(() => {
      closeWebview()
    })
  }

  /**
   * echarts tooltip在移动端没有手指放开就隐藏的选项，必须手动点击消失，这里处理为手动隐藏
   */
  const hideEchartsTooltip = () => {
    if (lineChartRef.current) {
      if (lineChartRef.current.childNodes[1]?.style.display === 'block') {
        lineChartRef.current.childNodes[1].style.display = 'none'
      }
    }
  }

  const addListener = () => {
    window.addEventListener('scroll', scrollAction, true)
  }

  const removeListener = () => {
    window.removeEventListener('scroll', scrollAction, true)
  }

  const scrollAction = () => {
    hideEchartsTooltip()
  }

  // 获取行程记录开启状态
  const fetchEnableStatus = () => {
    setIsLoading(true)
    // getEnableRecordStatus({ vin: queryVin })
    //   .then(res => {
    //     if (res.code === 200) {
    //       if (res.data) {
    //         initData()
    //       } else {
    //         showViewRecordConfirm()
    //       }
    //     } else {
    //       setIsLoading(false)
    //       setErrorMsg(DEFAULT_ERROR_MSG)
    //       setIsEmptyPage(true)
    //     }
    //   }).catch(err => {
    //     setIsLoading(false)
    //     setErrorMsg(DEFAULT_ERROR_MSG)
    //     setIsLoadingError(true)
    //   })
    getSettingQueryPromise()
      .then(res => {
        if (res?.moduleEnabled) {
          initData()
          sessionStorage.setItem('displayAddress',JSON.stringify(res?.displayAddress))
        } else {
          showViewRecordConfirm()
        }
      }).catch(err => {
        setIsLoading(false)
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
      })
  }

  const getSettingQueryPromise = (vin = queryVin) => {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const res: any = await getSettingsQuery({ vin })
        if (res.code === 200) {
          resolve(res.data)
        } else {
          reject()
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  const getUserInfo = (vin) => {
    return new Promise<any>(async (resolve, reject) => {
      try {
        const res: any = await getUserVehicleInfo({ vin })
        if (res.code === 200) {
          resolve(res.data)
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
          reject()
        }
      } catch (error) {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(error)
      }
    })
  }

  const getTripsListData = (params) => {
    return new Promise(async (resolve, reject) => {
      try {
        const res: any = await getTripsList(params)
        if (res.code === 200) {
          resolve(res.data)
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
          reject()
        }
      } catch (error) {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(error)
      }
    })
  }

  const getTripsSummaryData =(params) => {
    return new Promise(async (resolve, reject) => {
      try {
        const res: any = await getTripsSummary(params)
        if (res.code === 200) {
          resolve(res.data)
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
          reject()
        }
      } catch (error) {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(error)
      }
    })
  }

  const getTripsHighlightsData = (params) => {
    return new Promise(async (resolve, reject) => {
      try {
        const res: any = await getTripsHighlights(params)
        if (res.code === 200) {
          resolve(res.data)
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
          reject()
        }
      } catch (error) {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(error)
      }
    })
  }

  // 初始化页面数据
  const initData = async () => {
    setBeforeInit(false)
    setIsLoading(true)
    // 用户车辆信息
    let userInfo: any = {}
    try {
      const data = await getUserInfo(queryVin)
      // 用户信息中新增加字段allowRealTimeTrip 判断是否支持实时行程
      // 逻辑变化 1、支持实时行程不判断validTime 和 今天0点 行程未生成 2、支持实时行程7天和当月请求结束时间到now
      if (Array.isArray(data) && data.length > 0) {
        userInfo = data[0]

        if (!sessionStorage.getItem('firstRender')) {
          // 首页加载埋点
          reportData({pageId: 'P11180', buttonId: 'B001', vin: queryVin, params: {
            carType: userInfo.vehicleTypeSaleName
          }})
          sessionStorage.setItem('firstRender', 'true')
        }

        // 该车不支持查看行程数据
        if (!userInfo.supported) {
          setIsLoading(false)
          setErrorMsg('当前车辆暂不支持该功能')
          setIsEmptyPage(true)
          return
        }

        // 该车没有行程数据
        const todayTimestamp = new Date().setHours(0,0,0,0)
        // 不支持实时行程，才判断开始时间是否大于等于今天0点
        if (!userInfo.allowRealTimeTrip && userInfo.validStartTime >= todayTimestamp) {
          setIsLoading(false)
          setErrorMsg('行程记录暂未生成，明天再来看看哦')
          setIsEmptyPage(true)
          return
        }

        setVehicleInfo({
          vin: userInfo.vin,
          plateNo: userInfo.plateNo,
          vehicleTypeSaleName: userInfo.vehicleTypeSaleName,
          validStartTime: userInfo.validStartTime
        })

        // 设置是否支持实时行程
        allowRealTimeTrip.current = userInfo.allowRealTimeTrip
        // 获取当前时间周期选项
        getTimeRangeOptions(userInfo.validStartTime, userInfo.allowRealTimeTrip)

        setDateOptions({ label: timeRangeOptions.current.map(item => item.label) })

        // 若用户选中过时间周期，取缓存时间请求，否则默认使用最近七天发起请求
        const setRecent7Days = () => {
          setDateValues({ label: timeRangeOptions.current[0].label })
          setCurrentCycle(timeRangeOptions.current[0].label)
          reqTimeRanges = timeRangeOptions.current[0].value
        }

        const selectedTimeRange = sessionStorage.getItem('timeRange')
        let parsedTimeRange: any = {}
        let reqTimeRanges: number[] = []

        if (selectedTimeRange) {
          // 选中过时间周期
          parsedTimeRange = JSON.parse(selectedTimeRange)
          setDateValues({ label: parsedTimeRange.label })
          setCurrentCycle(parsedTimeRange.label)
          reqTimeRanges = parsedTimeRange.value
        } else {
          // 未选中过时间周期，首次进入，先判断url上是否带了month参数，若带了，使用url上的月份，否则默认使用最近七天
          if (queryMonth) {
            const strArr = queryMonth.split('-')
            const month = `${strArr[0]}年${Number(strArr[1])}月`
            isValidateMonth.current = timeRangeOptions.current.some(item => item.label === month)
            if (isValidateMonth.current) {
              setDateValues({ label: month })
              setCurrentCycle(month)
              reqTimeRanges = timeRangeOptions.current.filter(item => item.label === month)[0].value
            } else {
              setRecent7Days()
            }
          } else {
            setRecent7Days()
          }
        }

        searchData(reqTimeRanges)
      } else {
        // 当前用户是该车的授权使用人，data数组为空
        setIsLoading(false)
        setErrorMsg('暂不支持该功能')
        setIsEmptyPage(true)
      }
    } catch (error) {
      setIsLoading(false)
      setErrorMsg(DEFAULT_ERROR_MSG)
      setIsEmptyPage(true)
      console.log('initData error: ', error)
    }
  }

  const searchData = (timeRange: number[]) => {
    // 记录一份当前搜索的时间范围参数，提供其他场景作判断
    currentSearchTimeRange.current = timeRange
    // 实时新增逻辑 选择为7天且为实时行程时 请求summary接口截止时间应为昨天日期
    // 否则使用currentSearchTimeRange 中的数据
    const isSevenDays = !(currentSearchTimeRange.current[1] - currentSearchTimeRange.current[0] > 8 * 24 * 3600 * 1000)
    let summaryRequestEndTs = (isSevenDays && allowRealTimeTrip.current) ?
      new Date().setHours(0,0,0,0) - 1000 : currentSearchTimeRange.current[1]
    setIsLoading(true)
    Promise.all([
      getTripsListData({
        vin: queryVin,
        startTs: Number(timeRange[0]),
        endTs: Number(timeRange[1]),
        fetchSize: LIST_FETCH_SIZE
      }),
      getTripsSummaryData({
        vin: queryVin,
        startDate: formatTimestampToTxt(timeRange[0], 'YYYY-MM-DD'),
        // endDate: formatTimestampToTxt(timeRange[1], 'YYYY-MM-DD')
        endDate: formatTimestampToTxt(summaryRequestEndTs, 'YYYY-MM-DD')
      }),
      getTripsHighlightsData({
        vin: queryVin,
        startDate: formatTimestampToTxt(timeRange[0], 'YYYY-MM-DD'),
        endDate: formatTimestampToTxt(summaryRequestEndTs, 'YYYY-MM-DD')
      })
    ]).then((res: any) => {
      const list = formatListData(res[0])
      const trip = res[1]
      setListData(list)
      setTripsData(trip)
      setHighlightsData(res[2])

      // 折线图
      const lineInfo = formatLineData(
        trip.dailyTotalDriveMileageDetailList,
        trip.dailyAdMileageDetailList
      )
      setLineData(lineInfo.lineData)
      setXLabels(lineInfo.xLabels)

      setIsLoading(false)
      console.log('Promise.all succeed!')
    }).catch(error => {
      setIsLoading(false)
      console.log('Promise.all error -->', error)
    })
  }

  // 下拉到底部加载行程数据列表
  const onTripsListScroll = (e) => {
    const isScrollToBottom = e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 1
    if (isScrollToBottom && !isLoadingList.current && listData.length > 0) {
      loadListData()
    }
  }

  /**
   * 上拉加载，endTs每次根据获得的数据列表的最后一项的startTs作为下一次load的endTs参数，往前倒序查询
   */
  const loadListData = () => {
    if (!finishLoadList.current) {
      setLoadingList(true)
      isLoadingList.current = true
      const selectedTimeRange = timeRangeOptions.current.find(item => item.label === dateValues.label)!

      // 获取当前行程列表最后一项数组
      const listLastArr = listData[listData.length - 1]
      getTripsListData({
        vin: queryVin,
        startTs: Number(selectedTimeRange.value[0]),
        endTs: listLastArr[listLastArr.length - 1]?.startTs - 1, // 现在后端查询以开始时间聚类， 所以要在开始时间上减1
        fetchSize: LIST_FETCH_SIZE
      }).then((resData: any) => {
        if (resData && resData.length > 0 && !finishLoadList.current) {
          const data = concatListData(listData, formatListData(resData))
          setListData(data)
        } else {
          finishLoadList.current = true
        }
      }).catch(() => {})
      .finally(() => {
        setLoadingList(false)
        isLoadingList.current = false
      })
    }
  }

  /**
   * 按天聚合并格式化行程列表
   * @param list 
   */
  const formatListData = (list: any[]) => {
    const returnData: any = []
    const getItem = (item: any) => {
      return {
        tripId: item.tripId,
        type: item.type,
        startTs: item.startTs,
        endTs: item.endTs,
        startTime: formatTimestampToTxt(item.startTs, 'HH:mm'),
        endTime: formatTimestampToTxt(item.endTs, 'HH:mm'),
        date: formatTimestampToTxt(item.startTs, 'MM/DD'),
        startLocation: item.startLocation,
        endLocation: item.endLocation,
        adDistance: !isNaN(Number(item.adDistance)) ? (+item.adDistance).toFixed(1) : '--',
        driveDistance: !isNaN(Number(item.driveDistance)) ? (+item.driveDistance).toFixed(1) : '--',
        tripDuration: formatTripDuration(Number(item.tripDuration)),
        hasLocation: item.startLocation && item.endLocation && isSupportLocation, // 是否具有位置信息
        gapDays: getDaysApart(item.startTs, item.endTs) // 计算行程开始和结束时间的天数差
      }
    }

    if (Array.isArray(list) && list.length > 0) {
      list.forEach((item: any, index) => {
        if (index === 0) {
          returnData.push([getItem(item)])
        } else {
          // 是否聚合在同一天以当条行程的开始时间为准
          if (formatTimestampToTxt(item.startTs, 'MM/DD') === returnData[returnData.length - 1][0].date) {
            returnData[returnData.length - 1].push(getItem(item))
          } else {
            returnData.push([getItem(item)])
          }
        }
      })
    }

    // console.log('formatListData -->', returnData)

    return returnData
  }

  /**
   * 连接formatListData后的两组数据，用于上拉加载更新数据
   */
  const concatListData = (preData, curData) => {
    // 格式化加载后的数组如果第一项的日期和state里的数组最后一项日期相同，做一下聚合，再链接返回出去
    if (preData?.length && curData?.length) {
      if (preData[preData.length - 1][0].date === curData[0][0].date) {
        preData[preData.length - 1] = preData[preData.length - 1].concat(curData[0])
        preData = preData.concat(curData.slice(1))
      } else {
        preData = preData.concat(curData)
      }
    }
    return preData
  }

  /**
   * 格式化折线图数据
   * 服务端会完整响应起始到终止时间的数据，没数据的value返回null
   * @param totalMileageList 行驶里程折线数据
   * @param adMileageList 智驾里程折线数据
   */
  const formatLineData = (totalMileageList, adMileageList) => {
    // Y轴数据和X轴标签定义
    const lineData: { timeRange: string, timeList: string[], data: number[] }[] = []
    const xLabels: string[] = []

    // const mockLineData = [
    //   { timeRange: '行驶里程', timeList: ['6/15', '6/16', '6/17', '6/18', '6/19', '6/20', '6/21', '6/22' ,'6/23', '6/24'], data: [12,13,14,15,16,17,18,19,20,21] },
    //   { timeRange: '智驾里程', timeList: ['6/15', '6/16', '6/17', '6/18', '6/19', '6/20', '6/21', '6/22' ,'6/23', '6/24'], data: [3,4,5,6,7,8,9,10,11,12] },
    // ]

    // 行驶数据
    if (totalMileageList && totalMileageList?.length > 0) {
      lineData.push({
        timeRange: '行驶里程',
        timeList: totalMileageList.map(item => `${item.date?.split('-')[1]}/${item.date?.split('-')[2]}`),
        data: totalMileageList.map(item => +item.value)
      })

      // 时间格式 MM/DD 如06/15
      // X轴标签：如果选择了月份，只展示当月5、10、15、20、25号；如果选择最近7天，正常展示
      // 以行驶数据为准，设置X轴label数据

      // 判断当前的周期X轴标签format是按照最近7天还是月份
      // 因为实时行程搜索时间是今天 到7天前0点 所以判断是不是7天需要用大于8
      const isSevenDays = !(currentSearchTimeRange.current[1] - currentSearchTimeRange.current[0] > 8 * 24 * 3600 * 1000)
      lineData[0].timeList?.forEach(date => {
        if (isSevenDays) {
          xLabels.push(date)
        } else {
          if (['05', '10', '15', '20', '25'].indexOf(date.split('/')[1]) !== -1) {
            xLabels.push(date)
          } else {
            xLabels.push('')
          }
        }
      })
    }

    // 智驾数据
    if (adMileageList && adMileageList.length > 0) {
      lineData.push({
        timeRange: '辅助驾驶里程',
        timeList: adMileageList.map(item => `${item.date?.split('-')[1]}/${item.date?.split('-')[2]}`),
        data: adMileageList.map(item => +item.value)
      })
    }

    console.log('formatLineData -->', { lineData, xLabels })

    return {
      lineData,
      xLabels
    }
  }

  const handleChangeDate = (name, value) => {
    if (name === 'label') {
      setDateValues({ label: value })
    }
  }

  const cancelDateRange = () => {
    setShowDateRangePicker(false)
    setDateValues({ label: currentCycle })
  }

  const confirmDateRange = async () => {
    const selectedTimeRange = timeRangeOptions.current.find(item => item.label === dateValues.label)!
    setCurrentCycle(dateValues.label)
    setShowDateRangePicker(false)
    searchData(selectedTimeRange.value)

    // 切换时间周期需要重置列表加载完成状态，保障下一个周期的上拉加载
    finishLoadList.current = false

    console.log('confirmDateRange --> ', selectedTimeRange)
    // 存储用户当前选中的时间周期
    sessionStorage.setItem('timeRange', JSON.stringify(selectedTimeRange))

    // 时间切换埋点
    reportData({pageId: 'P11180', buttonId: 'B002', vin: queryVin, params: {
      carType: vehicleInfo.vehicleTypeSaleName,
      option: dateValues.label
    }})
  }

  const showMessageBox = (title: string, children: any, confirmButtonText = '知道了') => {
    MessageBox.alert({ title, confirmButtonText, children })
  }

  // const showNgpMileageTips = () => {
  //   showMessageBox('智驾里程', <div>1. 指行驶中使用了任意智驾功能的里程数，包括使用导航辅助驾驶或ACC/LCC功能。<br/>2. 百分比为占行驶里程的比例。</div>)
  //   // 智驾里程问号点击埋点
  //   reportData({pageId: 'P11180', buttonId: 'B004', vin: queryVin, params: {
  //     carType: vehicleInfo.vehicleTypeSaleName
  //   }})
  // }

  const showNgpDataTips = () => {
    showMessageBox('辅助驾驶数据', <div>统计期间未使用的辅助驾驶功能不作显示。</div>)
    // 智驾数据问号点击埋点
    reportData({pageId: 'P11180', buttonId: 'B005', vin: queryVin, params: {
      carType: vehicleInfo.vehicleTypeSaleName
    }})
  }

  // const showAdMileageTips = () => {
  //   showMessageBox('导航辅助驾驶', <div>1. 导航辅助驾驶包含高速NGP和城市NGP。<br/>2. 百分比为占行驶里程的比例。</div>)
  // }

  // const showAccLccTips = () => {
  //   showMessageBox('ACC/LCC', <div>1. 开启导航辅助驾驶功能时，ACC/LCC功能也会同步开启。<br/>2. 百分比为占行驶里程的比例。</div>)
  // }

  const handleClickTripItem = (item, vehicleInfo) => {
    debouncedClickRef.current(item, vehicleInfo)
  }

  const jumpToTripPage = (item, vehicleInfo) => {
    const pathname = path.replace('home', 'trip')  
    const paramsObj = {
      vin: vehicleInfo.vin,
      tripId: item.tripId,
      type: item.type,
      isSuperErev
    }
    const queryArr: string[] = []
    Object.keys(paramsObj).forEach(key => {
      queryArr.push(`${key}=${paramsObj[key]}`)
    })

    // 行程记录点击埋点
    const adMileageRate = !isNaN(Number(item.adDistance)) ? ((Number(item.adDistance) / Number(item.driveDistance)) * 100).toFixed(1) + '%' : '--'
    reportData({pageId: 'P11180', buttonId: 'B003', vin: queryVin, params: {
      carType: vehicleInfo.vehicleTypeSaleName,
      tripTime: item.startTs,
      smartDrivePer: adMileageRate
    }})
    // JSBridge调用原生跳转 原bridge会保持活跃所以不需要延迟跳转
    JSBridge.callHandler('openUrl', {
      url: `${window.location.origin}${pathname}?${queryArr.join('&')}`,
      data: {}
    }).catch(err => {
      console.error('JSBridge openUrl error: ', err)
    })
    // 延时一会儿跳转，等待埋点上报
    // setTimeout(() => {
    //   // window.location.href = `${window.location.origin}${pathname}?${queryArr.join('&')}`
    //   JSBridge.callHandler('openUrl', {
    //     url: `${window.location.origin}${pathname}?${queryArr.join('&')}`,
    //     data: {}
    //   }).catch(err => {
    //     console.error('JSBridge openUrl error: ', err)
    //   })
    // }, 300)
  }

  const [hasTransition, setHasTransition] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startY, setStartY] = useState(0)
  const [moveX, setMoveX] = useState(0)
  const [currentIndex, setCurrentIndex] = useState()
  const [currentSubIndex, setCurrentSubIndex] = useState()

  // 滑动距离阈值
  const SCROLL_THRESHOLD = 100

  const rem = parseInt(getComputedStyle(document.documentElement).fontSize)
  const distance = moveX > 0 ? 0 : -0.68 * rem
  const moveStyle = {
    transform: `translateX(${distance}PX)`,
    WebkitTransform: `translateX(${distance}PX)`,
    transition: 'transform 0.3s ease',
    WebkitTransition: 'transform 0.3s ease'
  }
  const noMoveStyle = {
    transform: `translateX(${0}PX)`,
    WebkitTransform: `translateX(${0}PX)`,
    transition: 'transform 0.3s ease',
    WebkitTransition: 'transform 0.3s ease'
  }

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].pageX)
    setStartY(e.touches[0].pageY)
  }
  const handleTouchMove = (e, index, subIndex) => {
    let moveY = 0
    let moveX = 0
    setCurrentIndex(index)
    setCurrentSubIndex(subIndex)
    moveY = e.touches[0].pageY - startY
    moveX = e.touches[0].pageX - startX
    setMoveX(moveX)
    // console.log('moveX moveY', moveX, moveY)
    if (Math.abs(moveY) > Math.abs(moveX)) {
      setHasTransition(false)
      return
    }
    if (Math.abs(moveX) < SCROLL_THRESHOLD) {
      setHasTransition(false)
    } else {
      setHasTransition(true)
    }
  }
  const delTripItem = (item, index, subIndex) => {
    MessageBox.confirm({
      title: '删除行程记录',
      message: '删除后不能恢复，但里程统计仍包含此行程。',
      cancelButtonText: '取消',
      confirmButtonText: '确定',
      className: 'message-box-del'
    }).then(() => {
      postRemoveTripData({ vin: queryVin, tripId: item.tripId, type: item.type }).then((res: any) => {
        if (res.code === 200) {
          if (listData[index].length === 1) {
            listData.splice(index, 1)
            setListData(listData.map(item => item))
            return
          }
          setListData(listData.map((dataArr, i) => {
            if (i === index) {
              dataArr.splice(subIndex, 1)
              return dataArr
            } else {
              return dataArr
            }
          }))
        } else {
          MessageBox.toast(DEFAULT_ERROR_MSG)
        }
      }).catch(() => {
        MessageBox.toast(DEFAULT_ERROR_MSG)
      }).finally(() => {
        setHasTransition(false)
      })
    }).catch(() => {
      setHasTransition(false)
    })
  }

  if (isLoading)
    return (
      <LoadingPage />
    )

  if (isLoadingError)
    return (
      <ErrorPlaceholder
        message={errorMsg}
      ></ErrorPlaceholder>
    )

  if (isEmptyPage)
    return (
      <EmptyPlaceholder text={errorMsg}></EmptyPlaceholder>
    )

  if (beforeInit) {
    return (
      <div className={styles.emptyPage}></div>
    )
  }

  const {
    vin,
    plateNo,
    vehicleTypeSaleName
  } = vehicleInfo

  // 服务端返回的数据都是保留一位小数的字符串，判空要转换成数字
  // const hideAdData = !+tripsData.totalNgpMileage && !+tripsData.totalAccLccMileage && !+tripsData.apaTimes && !+tripsData.vpaTimes
  const intelligentDriveDataArr = [
    ...formatAdParkingData({
      vpaTimes: tripsData.vpaTimes,
      apaTimes: tripsData.apaTimes,
    }),
    ...formatHighlightData(highlightsData, 4)
  ]
  // totalNgpMileage totalAccLccMileage 这两个字段不再使用 判断条件改为totalAdMileage 和 intelligentDriveDataArr.length === 0
  const hideAdData = !+tripsData.totalAdMileage && intelligentDriveDataArr.length === 0

  // const isShortPadding = [1,3].indexOf([
  //   +tripsData.totalNgpMileage,
  //   +tripsData.totalAccLccMileage,
  //   +tripsData.apaTimes,
  //   +tripsData.vpaTimes
  // ].filter(item => Boolean(item)).length) === -1
  // const validCount = [
  //   +tripsData.totalNgpMileage,
  //   +tripsData.totalAccLccMileage,
  //   +tripsData.apaTimes,
  //   +tripsData.vpaTimes
  // ].filter(item => Boolean(item)).length
  const summatryDataList = [
    +tripsData.totalNgpMileage,
    +tripsData.totalAccLccMileage,
    +tripsData.apaTimes,
    +tripsData.vpaTimes
  ]
  const validCount = summatryDataList.filter(item => Boolean(item)).length
  const isShortPadding = ![1,3].includes(validCount)

  return (
    <div className={styles.rootPage} onScroll={onTripsListScroll}>
      {
        !!announcementInfo && (
          <Alert
            message={announcementInfo.text}
            type={announcementInfo.isUrgent ? 'error' : 'warning'}
            closable
            onClick={() => {
              jumpPage(announcementInfo.link)
            }}
            onClose={(e) => {
              e.stopPropagation()
              console.log('关闭了公告栏~~');
              // 存储当前生效公告id，关闭当前公告后不再展示，除非下一次请求到新的公告id
              localStorage.setItem('deployId', announcementInfo.deployId)
            }}
          />
        )
      }
      <div className={styles.homePage}>
        <div className={styles.topbar} onClick={hideEchartsTooltip}>
          <div className={styles.vehicleInfo}>
            <div className={styles.plateNo}>
              {vehicleTypeSaleName} {plateNo ? formatVehiclePlate(plateNo) : desensitizedVin(vin)}
            </div>
          </div>
          <div className={styles.weekInfo} onClick={() => setShowDateRangePicker(true)}>
            <div className={styles.currentWeek}>{currentCycle}</div>
            <div className={styles.arrowDown}></div>
          </div>
        </div>
        <div className={styles.summaryArea}>
          <div className={styles.summaryDataRow}>
            <div className={styles.summaryData}>
              <div className={styles.data}>{tripsData.totalMileage}</div>
              <div className={styles.name}>{PROP_DICT['totalMileage']}</div>
            </div>
            <div className={styles.summaryData}>
              <div className={styles.data}>
                {tripsData.totalAdMileage}
                {
                  !!+tripsData.totalAdMileage && (
                    <div className={styles.rate}>
                      {+tripsData.adMileageRate === 0 ? '<1' : tripsData.adMileageRate}%
                    </div>
                  )
                }
              </div>
              <div className={styles.name}>{PROP_DICT['totalAdMileage']}</div>
            </div>
            <div className={styles.summaryData}>
              <div className={styles.data}>{+tripsData.totalDuration > 0 ? (tripsData.totalDuration / 3600).toFixed(1) : (+tripsData.totalDuration).toFixed(1)}</div>
              <div className={styles.name}>{PROP_DICT['totalDuration']}</div>
            </div>
          </div>
          <div className={styles.mileageTag}>{'里程(km)'}</div>
          <div className={styles.chartContainer} ref={lineChartRef}></div>
          {/* <div className={styles.lineDataTip}>不显示统计期间未使用的自动驾驶功能</div> */}
        </div>
        <div className={styles.detailArea}>
          <div className={styles.title} onClick={showNgpDataTips}>
            <div className={styles.titleText}>辅助驾驶数据</div>
            <img className={styles.helpIcon} alt='' src={require('../assets/help.png')}></img>
          </div>
          {
            hideAdData ? 
            <div className={styles.emptyData}>
              <img className={styles.emptyImage} alt='' src={require('../assets/empty.png')}></img>
              <div className={styles.emptyDesc}>统计期间未使用辅助驾驶功能</div>
            </div> :
            <IntelligentDriveData
              intelligentDriveDataArr={intelligentDriveDataArr}
            />
          }
        </div>
        <div className={styles.tripsRecordArea}>
          <div className={styles.title}>行程记录</div>
          {
            listData?.length > 0 ? (
              <div>
                {
                  listData.length > 0 && listData.map((item, index) => (
                    <div className={styles.singleDate} key={index}>
                      <div className={styles.date}>{item[0].date}</div>
                      <div>
                        {
                          item.length > 0 && item.map((subItem, subIndex) => (
                            <div className={styles.tripBar} key={subIndex}>
                              <div className={styles.tripShow}
                                onClick={() => handleClickTripItem(subItem, vehicleInfo)} 
                                onTouchStart={(e) => handleTouchStart(e)}
                                onTouchMove={(e) => handleTouchMove(e, index, subIndex)}
                                style={(hasTransition && currentIndex === index && currentSubIndex === subIndex) ? moveStyle : noMoveStyle}
                              >
                                <div className={styles.dataContent}></div>
                                { !subItem.hasLocation &&
                                  <div className={styles.timeHeader}>{subItem.startTime}</div>
                                }
                                
                                <div className={styles.tripData}>
                                  <span className={styles.data}>{`行驶 ${subItem.driveDistance}km`}</span>
                                  <span className={styles.dividingLine}>|</span>
                                  <span className={styles.data}>{`辅助驾驶 ${subItem.adDistance}km`}</span>
                                  <span className={styles.dividingLine}>|</span>
                                  <span className={styles.data}>
                                    {`时长 ${subItem.tripDuration.time}${subItem.tripDuration.unit}`}
                                  </span>
                                  <img src={require('../assets/enter.png')} alt='' className={styles.iconRight}></img>
                                  {/* <span className={styles.drive}>{
                                    `行驶 ${subItem.driveDistance} `}
                                    <span className={styles.unit}>km</span>
                                  </span>
                                  <span className={styles.adDrive}>
                                    {`智驾 ${subItem.adDistance} `}
                                    <span className={styles.unit}>km</span>
                                  </span>
                                  <span className={styles.duration}>
                                    {`时长 ${subItem.tripDuration.time} `}
                                    <span className={styles.unit}>{subItem.tripDuration.unit}</span>
                                  </span> */}
                                </div>
                                { subItem.hasLocation && <>
                                  <div className={styles.locationContainer}>
                                    <div className={`${styles.iconContainer} ${styles.paddingTop}`}>
                                      <div className={`${styles.bigCircle} ${styles.start}`}></div>
                                      <div className={styles.dashCircle}></div>
                                      <div className={styles.dashCircle}></div>
                                    </div>
                                    <div className={styles.locationContent}>
                                      <div className={styles.locationTime}>{subItem.startTime}</div>
                                      <div className={styles.locationContnentText}>
                                        {subItem.startLocation}
                                      </div>
                                    </div>
                                  </div>
                                  <div className={styles.locationContainer}>
                                    <div className={`${styles.iconContainer} ${styles.paddingBottom}`}>
                                    <div className={styles.dashCircle}></div>
                                    <div className={styles.dashCircle}></div>
                                      <div className={`${styles.bigCircle} ${styles.end}`}></div>
                                    </div>
                                    <div className={styles.locationContent}>
                                      <div className={styles.locationTime}>
                                        {subItem.endTime}
                                        {subItem.gapDays > 0 &&
                                          <div className={styles.gapDays}>+{subItem.gapDays}</div>
                                        }
                                      </div>
                                      <div className={styles.locationContnentText}>
                                        {subItem.endLocation}
                                      </div>
                                    </div>
                                  </div>
                                </>}
                              </div>
                              <div
                                className={`${styles.delBtn} ${subItem.hasLocation ? styles.bigHeight : ''}`}
                                onClick={() => delTripItem(subItem, index, subIndex)}
                              >删除</div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className={styles.emptyData}>
                <img className={styles.emptyImage} alt='' src={require('../assets/empty.png')}></img>
                <div className={styles.emptyDesc}>暂无行程记录</div>
              </div>
            )
          }
          {
            loadingList && (
              <div className={styles.loadingMore}>
                <LoadingMore loadingText="正在加载"/>
              </div>
            )
          }
        </div>
        {showDateRangePicker && (
          <Picker
            title="周期选择"
            onCancel={cancelDateRange}
            onConfirm={confirmDateRange}
            onChange={handleChangeDate}
            valueGroups={dateValues}
            optionGroups={dateOptions}
            itemHeight={48}
          ></Picker>
        )}
      </div>
    </div>
  )
}

export default App